interface Rule {
  field: string;
  operator:
    | "="
    | "!="
    | ">"
    | "<"
    | ">="
    | "<="
    | "contains"
    | "beginsWith"
    | "endsWith"
    | "doesNotContain"
    | "doesNotBeginWith"
    | "doesNotEndWith"
    | "null"
    | "notNull"
    | "in"
    | "notIn"
    | "between"
    | "notBetween";
  value: any; // eslint-disable-line
  valueSource?: "value" | "variable";
}

export interface Logic {
  combinator: "and" | "or";
  not?: boolean;
  rules: (Rule | Logic)[];
}

interface LessonResponse {
  [key: string]: {
    label: string;
    value: any; // eslint-disable-line
  };
}

/**
 * Evaluates a single rule.
 */
const evaluateRule = (rule: Rule, responses: LessonResponse): boolean => {
  const { field, operator, value } = rule;
  let responseValue = responses[field]?.value;

  // Normalize responseValue and comparisonValue for case-insensitive comparisons
  if (typeof responseValue === "string") {
    responseValue = responseValue.toLowerCase();
  } else if (Array.isArray(responseValue)) {
    responseValue = responseValue.map((item) =>
      typeof item === "string" ? item.toLowerCase() : item,
    );
  }

  const comparisonValue =
    typeof value === "string"
      ? value.toLowerCase()
      : Array.isArray(value)
        ? value.map((item) =>
            typeof item === "string" ? item.toLowerCase() : item,
          )
        : value;

  let isMatch = false;

  if (Array.isArray(responseValue)) {
    switch (operator) {
      case "=":
        isMatch = responseValue.includes(comparisonValue);
        break;
      case "!=":
        isMatch = !responseValue.includes(comparisonValue);
        break;
      case ">":
        isMatch = responseValue.some((item) => item > comparisonValue);
        break;
      case "<":
        isMatch = responseValue.some((item) => item < comparisonValue);
        break;
      case ">=":
        isMatch = responseValue.some((item) => item >= comparisonValue);
        break;
      case "<=":
        isMatch = responseValue.some((item) => item <= comparisonValue);
        break;
      case "contains":
        isMatch = responseValue.some((item) => item.includes(comparisonValue));
        break;
      case "doesNotContain":
        isMatch = responseValue.every(
          (item) => !item.includes(comparisonValue),
        );
        break;
      case "beginsWith":
        isMatch = responseValue.some((item) =>
          item.startsWith(comparisonValue),
        );
        break;
      case "doesNotBeginWith":
        isMatch = responseValue.every(
          (item) => !item.startsWith(comparisonValue),
        );
        break;
      case "endsWith":
        isMatch = responseValue.some((item) => item.endsWith(comparisonValue));
        break;
      case "doesNotEndWith":
        isMatch = responseValue.every(
          (item) => !item.endsWith(comparisonValue),
        );
        break;
      case "null":
        isMatch =
          responseValue.length === 0 ||
          responseValue.every(
            (item) => item === null || item === "" || item === undefined,
          );
        break;
      case "notNull":
        isMatch =
          responseValue.length > 0 &&
          responseValue.some(
            (item) => item !== null && item !== "" && item !== undefined,
          );
        break;
      case "in":
        isMatch = comparisonValue.includes(responseValue);
        break;
      case "notIn":
        isMatch = !comparisonValue.includes(responseValue);
        break;
      case "between": {
        const [lowerBound, upperBound] = comparisonValue
          .split(",")
          .map((v) => Number(v.trim()));
        isMatch = responseValue.some(
          (item) => item >= lowerBound && item <= upperBound,
        );
        break;
      }
      case "notBetween": {
        const [lowerBound, upperBound] = comparisonValue
          .split(",")
          .map((v) => Number(v.trim()));
        isMatch = responseValue.every(
          (item) => item < lowerBound || item > upperBound,
        );
        break;
      }
      default:
        isMatch = false;
    }
  } else {
    switch (operator) {
      case "=":
        isMatch = responseValue === comparisonValue;
        break;
      case "!=":
        isMatch = responseValue !== comparisonValue;
        break;
      case ">":
        isMatch = responseValue > comparisonValue;
        break;
      case "<":
        isMatch = responseValue < comparisonValue;
        break;
      case ">=":
        isMatch = responseValue >= comparisonValue;
        break;
      case "<=":
        isMatch = responseValue <= comparisonValue;
        break;
      case "contains":
        isMatch =
          typeof responseValue === "string" &&
          responseValue.includes(comparisonValue);
        break;
      case "doesNotContain":
        isMatch =
          typeof responseValue === "string" &&
          !responseValue.includes(comparisonValue);
        break;
      case "beginsWith":
        isMatch =
          typeof responseValue === "string" &&
          responseValue.startsWith(comparisonValue);
        break;
      case "doesNotBeginWith":
        isMatch =
          typeof responseValue === "string" &&
          !responseValue.startsWith(comparisonValue);
        break;
      case "endsWith":
        isMatch =
          typeof responseValue === "string" &&
          responseValue.endsWith(comparisonValue);
        break;
      case "doesNotEndWith":
        isMatch =
          typeof responseValue === "string" &&
          !responseValue.endsWith(comparisonValue);
        break;
      case "null":
        isMatch =
          responseValue === null ||
          responseValue === "" ||
          responseValue === undefined;
        break;
      case "notNull":
        isMatch =
          responseValue !== null &&
          responseValue !== "" &&
          responseValue !== undefined;
        break;
      case "in":
        isMatch = comparisonValue.includes(responseValue);
        break;
      case "notIn":
        isMatch = !comparisonValue.includes(responseValue);
        break;
      case "between": {
        const [lowerBound, upperBound] = comparisonValue
          .split(",")
          .map((v) => Number(v.trim()));
        isMatch = responseValue >= lowerBound && responseValue <= upperBound;
        break;
      }
      case "notBetween": {
        const [lowerBound, upperBound] = comparisonValue
          .split(",")
          .map((v) => Number(v.trim()));
        isMatch = responseValue < lowerBound || responseValue > upperBound;
        break;
      }
      default:
        isMatch = false;
    }
  }

  return isMatch;
};

/**
 * Recursively evaluates nested logic.
 */
export const evaluateLogic = (
  logic: Logic,
  responses: LessonResponse,
): boolean => {
  const results = logic.rules.map((rule) => {
    if ("combinator" in rule) {
      return evaluateLogic(rule, responses);
    } else {
      return evaluateRule(rule, responses);
    }
  });

  const baseResult =
    logic.combinator === "and"
      ? results.every((result) => result)
      : results.some((result) => result);

  return baseResult;
};
